import { useEffect, useMemo, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { formatCurrency } from '~/utils/paymentUtils';
import type { SchoolInvoiceTableRow } from './invoiceRules';
import { useDeleteSchoolDrafts, type DeleteSchoolDraftsPayload } from './useDeleteSchoolDrafts';

interface DeleteSchoolDraftsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolYearId: string;
  /** The whole selection; the backend skips whatever is not a draft. */
  invoices: readonly SchoolInvoiceTableRow[];
  onCompleted: (payload: DeleteSchoolDraftsPayload) => void;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export function DeleteSchoolDraftsDialog({
  open,
  onOpenChange,
  schoolYearId,
  invoices,
  onCompleted,
}: DeleteSchoolDraftsDialogProps) {
  const drafts = useMemo(() => invoices.filter((row) => row.status === 'DRAFT'), [invoices]);
  const skippedCount = invoices.length - drafts.length;
  const totalCents = useMemo(() => drafts.reduce((sum, row) => sum + row.totalCents, 0), [drafts]);
  const [error, setError] = useState<string | null>(null);
  const [deleteSchoolDrafts, { loading }] = useDeleteSchoolDrafts();

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleConfirm = async () => {
    setError(null);
    const response = await deleteSchoolDrafts({
      variables: { input: { schoolYearId, invoiceIds: invoices.map((row) => row.invoiceId) } },
    });
    const payload = response.data?.deleteSchoolDrafts;
    if (!payload) {
      setError('The delete failed. Nothing was removed.');
      return;
    }
    if (payload.families.length === 0) {
      setError(payload.userErrors?.[0]?.message ?? 'The delete failed. Nothing was removed.');
      return;
    }
    onCompleted(payload);
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!loading) onOpenChange(next); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {plural(drafts.length, 'draft', 'drafts')}</AlertDialogTitle>
          <AlertDialogDescription>
            Delete {plural(drafts.length, 'draft', 'drafts')} totalling {formatCurrency(totalCents)}? This cannot be
            undone. Posted invoices in the selection are left alone.
            {skippedCount > 0 && (
              <>
                {' '}
                {skippedCount} of the {plural(invoices.length, 'selected row', 'selected rows')}{' '}
                {skippedCount === 1 ? 'is not a draft' : 'are not drafts'} and will be skipped.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              void handleConfirm();
            }}
            disabled={loading || drafts.length === 0}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {loading ? 'Deleting...' : `Delete ${plural(drafts.length, 'draft', 'drafts')}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
